import React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import SelectionButton from './SelectionButton';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

describe('SelectionButton', () => {
  const defaultProps = {
    title: 'Title',
    description: 'Short description',
    icon: 'sparkles',
  };

  it('renders title and description', () => {
    render(<SelectionButton {...defaultProps} />);
    expect(screen.getByText('Title')).toBeInTheDocument();
    expect(screen.getByText('Short description')).toBeInTheDocument();
  });

  it('renders as button', () => {
    render(<SelectionButton {...defaultProps} />);
    expect(screen.getByRole('button')).toBeInTheDocument();
  });

  it('handles click events', () => {
    const onClick = jest.fn();
    render(<SelectionButton {...defaultProps} onClick={onClick} />);
    fireEvent.click(screen.getByRole('button'));
    expect(onClick).toHaveBeenCalled();
  });

  it('applies selected state', () => {
    const { container } = render(<SelectionButton {...defaultProps} selected={true} />);
    expect(container.querySelector('.selected')).toBeInTheDocument();
  });

  it('applies custom className', () => {
    const { container } = render(<SelectionButton {...defaultProps} containerClassName="custom" />);
    expect(container.querySelector('.custom')).toBeInTheDocument();
  });

  it('renders icon', () => {
    const { container } = render(<SelectionButton {...defaultProps} />);
    expect(container.querySelector('.iconContainer')).toBeInTheDocument();
    expect(container.querySelector('.iconContainer')).not.toBeEmptyDOMElement();
  });

  it('renders with id', () => {
    const { container } = render(<SelectionButton {...defaultProps} id="test" />);
    expect(container.querySelector('#test')).toBeInTheDocument();
  });

  it('renders children when selected', () => {
    render(
      <SelectionButton {...defaultProps} selected={true}>
        <div data-testid="child">Content</div>
      </SelectionButton>
    );
    expect(screen.getByTestId('child')).toBeInTheDocument();
  });

  it('renders input fields when selected', () => {
    const inputFields = [{ label: 'Test', placeholder: 'Enter value' }];
    render(<SelectionButton {...defaultProps} selected={true} inputFields={inputFields} />);
    expect(screen.getByLabelText('Test')).toBeInTheDocument();
  });

  it('applies context class', () => {
    const { container } = render(<SelectionButton {...defaultProps} context="company" />);
    expect(container.querySelector('.company')).toBeInTheDocument();
  });

  it('applies neutral context class', () => {
    const { container } = render(<SelectionButton {...defaultProps} context="neutral" />);
    expect(container.querySelector('.neutral')).toBeInTheDocument();
  });

  it('renders as link when href is provided', () => {
    render(<SelectionButton {...defaultProps} href="/test" />);
    expect(screen.getByRole('link')).toBeInTheDocument();
  });

  it('handles keyboard navigation', () => {
    const onClick = jest.fn();
    render(<SelectionButton {...defaultProps} onClick={onClick} />);
    const button = screen.getByRole('button');

    fireEvent.keyDown(button, { key: 'Enter' });
    expect(onClick).toHaveBeenCalled();

    onClick.mockClear();
    fireEvent.keyDown(button, { key: ' ' });
    expect(onClick).toHaveBeenCalled();
  });

  describe('accessibility', () => {
    it('has aria-label matching the title', () => {
      render(<SelectionButton {...defaultProps} />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-label', 'Title');
    });

    it('has aria-pressed=false when not selected', () => {
      render(<SelectionButton {...defaultProps} />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-pressed', 'false');
    });

    it('has aria-pressed=true when selected', () => {
      render(<SelectionButton {...defaultProps} selected />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-pressed', 'true');
    });

    it('does not set aria-pressed on link variant', () => {
      render(<SelectionButton {...defaultProps} href="/test" />);
      expect(screen.getByRole('link')).not.toHaveAttribute('aria-pressed');
    });

    it('marks icon as aria-hidden', () => {
      const { container } = render(<SelectionButton {...defaultProps} />);
      expect(container.querySelector('[data-icon="sparkles"]')).toHaveAttribute('aria-hidden', 'true');
    });

    it('is focusable', () => {
      render(<SelectionButton {...defaultProps} />);
      expect(screen.getByRole('button')).toHaveAttribute('tabIndex', '0');
    });

    it('does not render hidden input when not selected', () => {
      const { container } = render(<SelectionButton {...defaultProps} name="option" value="a" />);
      const hidden = container.querySelector('input[type="hidden"]') as HTMLInputElement;
      expect(hidden.value).toBe('');
    });

    it('renders hidden input with value when selected', () => {
      const { container } = render(<SelectionButton {...defaultProps} name="option" value="a" selected />);
      const hidden = container.querySelector('input[type="hidden"]') as HTMLInputElement;
      expect(hidden.value).toBe('a');
    });

    it('does not render children when not selected', () => {
      render(
        <SelectionButton {...defaultProps}>
          <div data-testid="child">Content</div>
        </SelectionButton>
      );
      expect(screen.queryByTestId('child')).not.toBeInTheDocument();
    });
  });
});
